import { PlusCircle } from "react-feather";
import { Link } from "react-router-dom";
import { Select, Modal, Form, Input, Button, Table, Popconfirm, Upload, message } from "antd";
import React, { useState, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import moment from "moment";
import {
  submitApplication,
  getAllApplications,
  getApplicationById,
  updateApplicationStatus,
  deleteApplication,
  getAllJobPostings,
  searchApplications,
  getResumeDetails,
  matchQualifications,
} from "../../service/recruitmentService";

const Applications = () => {
  const [form] = Form.useForm();
  const [openApplyModal, setOpenApplyModal] = useState(false);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [openMatchModal, setOpenMatchModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [applications, setApplications] = useState([]);
  const [jobPostings, setJobPostings] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0 });
  const [pageSizeModalVisible, setPageSizeModalVisible] = useState(false);
  const [tempPageSize, setTempPageSize] = useState(pagination.pageSize);
  const [jobTitle, setJobTitle] = useState("");
  const [candidateName, setCandidateName] = useState("");
  const [status, setStatus] = useState("");
  const [resumeText, setResumeText] = useState("");
  const [matchResults, setMatchResults] = useState(null);
  const pageSizeOptions = [5, 10, 20, 50, 100];

  useEffect(() => {
    const fetchApplications = async () => {
      setLoading(true);
      const data = await getAllApplications();
      setLoading(false);
      if (Array.isArray(data)) {
        setApplications(data);
        setPagination((prev) => ({ ...prev, total: data.length }));
      } else {
        setApplications([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
      }
    };
    fetchApplications();
  }, []);

  useEffect(() => {
    const fetchJobPostings = async () => {
      const data = await getAllJobPostings();
      if (Array.isArray(data)) {
        setJobPostings(data.filter((job) => job.status === "Open"));
      } else {
        setJobPostings([]);
      }
    };
    fetchJobPostings();
  }, []);

  const fetchApplications = async () => {
    setLoading(true);
    const data = await getAllApplications();
    setLoading(false);
    if (Array.isArray(data)) {
      setApplications(data);
      setPagination((prev) => ({ ...prev, total: data.length }));
    } else {
      setApplications([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    }
  };

  const handleSearchApplications = async () => {
    setLoading(true);
    try {
      const data = await searchApplications({ jobTitle, candidateName, status });
      if (Array.isArray(data)) {
        setApplications(data);
        setPagination((prev) => ({ ...prev, total: data.length, current: 1 }));
      } else {
        setApplications([]);
        setPagination((prev) => ({ ...prev, total: 0, current: 1 }));
      }
    } catch (error) {
      message.error("Error searching applications: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handlePageSizeChange = () => {
    const newPagination = { ...pagination, pageSize: tempPageSize, current: 1 };
    setPagination(newPagination);
    setPageSizeModalVisible(false);
  };

  const onApplyFinish = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      if (!values.job_id) {
        message.error("Job selection is required");
        setLoading(false);
        return;
      }
      if (!values.candidate_name) {
        message.error("Candidate name is required");
        setLoading(false);
        return;
      }
      if (!values.email) {
        message.error("Email is required");
        setLoading(false);
        return;
      }
      if (!values.resume || !values.resume.fileList || !values.resume.fileList[0]) {
        message.error("Please upload a resume");
        setLoading(false);
        return;
      }

      formData.append("job_id", values.job_id);
      formData.append("candidate_name", values.candidate_name);
      formData.append("email", values.email);
      formData.append("resume", values.resume.fileList[0].originFileObj);
      if (values.qualifications && values.qualifications.length > 0) {
        formData.append("qualifications", JSON.stringify(values.qualifications));
      }

      // Log FormData for debugging
      const formDataEntries = [...formData.entries()];
      console.log("FormData entries:", formDataEntries);

      const result = await submitApplication(formData);
      if (result) {
        message.success("Application submitted successfully");
        await fetchApplications();
        setOpenApplyModal(false);
        form.resetFields();
      } else {
        message.error("Failed to submit application");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Unknown error";
      message.error(`Error submitting application: ${errorMessage}`);
      console.error("Submission error details:", error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    setLoading(true);
    try {
      await updateApplicationStatus(id, status);
      await fetchApplications();
      message.success("Status updated successfully");
    } catch (error) {
      message.error("Error updating status: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (id) => {
    setLoading(true);
    try {
      const applicationData = await getApplicationById(id);
      if (applicationData) {
        setSelectedApplication(applicationData);
        try {
          const resumeData = await getResumeDetails(id);
          setResumeText(resumeData.extractedText || "No resume text available");
        } catch (resumeError) {
          console.error("Error fetching resume details:", resumeError);
          setResumeText("Failed to load resume text. Please ensure the application has a valid resume.");
        }
        setOpenDetailsModal(true);
      } else {
        message.error("Failed to fetch application details");
      }
    } catch (error) {
      message.error("Error fetching details: " + error.message);
      setResumeText("");
    } finally {
      setLoading(false);
    }
  };

  const handleMatchQualifications = async (applicationId, jobId) => {
    setLoading(true);
    try {
      const result = await matchQualifications(applicationId, jobId);
      setMatchResults(result);
      setOpenMatchModal(true);
    } catch (error) {
      message.error("Error matching qualifications: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadResume = (resume) => {
    try {
      if (!resume.data || typeof resume.data !== "string") {
        throw new Error("Base64 data is missing or invalid");
      }
      if (!/^[A-Za-z0-9+/=]+$/.test(resume.data)) {
        throw new Error("Base64 string contains invalid characters");
      }
      console.log("Base64 length:", resume.data.length, "First 50 chars:", resume.data.substring(0, 50));

      const byteCharacters = atob(resume.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], { type: resume.contentType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = resume.originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error, "Base64 sample:", resume.data?.substring(0, 50));
      message.error("Failed to download resume: " + error.message);
    }
  };

  const columns = [
    {
      title: "Job Title",
      dataIndex: "job_id",
      key: "job_id",
      render: (job_id) => job_id?.title || "N/A",
    },
    {
      title: "Candidate Name",
      dataIndex: "candidate_name",
      key: "candidate_name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Qualifications",
      dataIndex: "qualifications",
      key: "qualifications",
      render: (qualifications) =>
        qualifications?.length
          ? qualifications.map((q) => `${q.name}: ${q.level}`).join(", ")
          : "N/A",
    },
    {
  title: "Status",
  dataIndex: "status",
  key: "status",
  render: (status, record) => (
    <Select
      value={status}
      onChange={(value) => handleStatusChange(record._id, value)}
      style={{
        width: '100%',
        color:
          status === "Pending" ? "#FFA500" : // Orange for Pending
          status === "Reviewed" ? "#1E90FF" : // Blue for Reviewed
          status === "Accepted" ? "#008000" : // Green for Accepted
          status === "Rejected" ? "#FF0000" : // Red for Rejected
          "inherit",
      }}
      dropdownStyle={{ minWidth: 120 }}
    >
      {["Pending", "Reviewed", "Rejected", "Accepted"].map((s) => (
        <Select.Option
          key={s}
          value={s}
          style={{
            color:
              s === "Pending" ? "#FFA500" :
              s === "Reviewed" ? "#1E90FF" :
              s === "Accepted" ? "#008000" :
              s === "Rejected" ? "#FF0000" :
              "inherit",
          }}
        >
          {s}
        </Select.Option>
      ))}
    </Select>
  ),
},
    {
      title: "Applied Date",
      dataIndex: "applied_at",
      key: "applied_at",
      render: (applied_at) => (applied_at ? moment(applied_at).format("YYYY-MM-DD") : "N/A"),
    },
    {
      title: "Action",
      key: "action",
      render: (record) => (
        <td className="action-table-data">
          <Link
            className="me-2 p-2"
            to="#"
            onClick={() => handleViewDetails(record._id)}
            title="View application details"
          >
            <i data-feather="eye" className="feather-eye"></i>
          </Link>
          <Link
            className="me-2 p-2"
            to="#"
            onClick={() => handleMatchQualifications(record._id, record.job_id._id)}
            title="Match qualifications"
          >
            <i data-feather="check-circle" className="feather-check-circle"></i>
          </Link>
          <Popconfirm
            title="Delete the Application"
            description="Are you sure to delete this application?"
            onConfirm={async () => {
              try {
                await deleteApplication(record._id);
                await fetchApplications();
                message.success("Application deleted successfully");
              } catch (error) {
                message.error("Error deleting application: " + error.message);
              }
            }}
            onCancel={() => console.log("Cancelled")}
            okText="Yes"
            cancelText="No"
          >
            <Link className="confirm-text p-2" title="Delete application">
              <i data-feather="trash-2" className="feather-trash-2"></i>
            </Link>
          </Popconfirm>
        </td>
      ),
    },
  ];

  const renderJobOption = (job) => (
    <div className="p-2">
      <div className="font-semibold">{job.title}</div>
      <div className="text-sm">Requirements: {job.requirements?.length ? job.requirements.join(", ") : "N/A"}</div>
      <div className="text-sm">Qualifications: {job.qualifications?.length ? job.qualifications.map((q) => `${q.name}: ${q.expectedLevel}`).join(", ") : "N/A"}</div>
      <div className="text-sm">Due Date: {job.dueDate ? moment(job.dueDate).format("YYYY-MM-DD") : "N/A"}</div>
    </div>
  );

  return (
    <div className="page-wrapper-lt">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="header-leave-container">
              <div>
                <ArrowLeft size={32} />
              </div>
              <div className="page-title">
                <h4>Applications</h4>
                <h6>Apply for jobs and track applications</h6>
              </div>
            </div>
            <div className="page-btn">
              <a
                className="btn btn-added-grid flex items-center justify-center"
                onClick={() => setOpenApplyModal(true)}
                title="Apply for a job"
              >
                <PlusCircle className="mr-2 h-5 w-5" />
                Upload
              </a>
            </div>
          </div>
        </div>

        <div className="card table-list-card-grid">
          <div className="card-body" style={{ maxWidth: "100%", marginRight: "20px" }}>
            <div
              className="container mt-3"
              style={{ maxWidth: "100%", marginLeft: "20px", marginRight: "40px" }}
            >
              <div className="row d-flex flex-wrap align-items-start justify-content-between">
                <div className="col-12 col-md-3 text-start mb-4">
                  <label className="form-label font-medium">Job Title</label>
                  <Input
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    placeholder="Enter job title"
                    className="h-10"
                  />
                </div>
                <div className="col-12 col-md-3 mb-4 text-start">
                  <label className="form-label font-medium">Candidate Name</label>
                  <Input
                    value={candidateName}
                    onChange={(e) => setCandidateName(e.target.value)}
                    placeholder="Enter candidate name"
                    className="h-10"
                  />
                </div>
                <div className="col-12 col-md-3 mb-4 text-start">
                  <label className="form-label font-medium">Status</label>
                  <Select
                    value={status}
                    onChange={(value) => setStatus(value)}
                    placeholder="Select status"
                    allowClear
                    className="h-10"
                  >
                    {["Pending", "Reviewed", "Rejected", "Accepted"].map((s) => (
                      <Select.Option key={s} value={s}>
                        {s}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
            <div
              className="container mt-0"
              style={{ maxWidth: "100%", marginLeft: "20px", marginRight: "40px" }}
            >
              <div className="row d-flex align-items-center justify-content-start">
                <div className="col-12 col-md-2 mb-3 text-start">
                  <Button
                    disabled={loading}
                    className="btn btn-submit h-10 w-full"
                    onClick={handleSearchApplications}
                  >
                    {loading ? "Searching..." : "Search"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card table-list-card-grid">
          <div className="card-body">
            <div className="table-top"></div>
            <div className="table-responsive">
              <Table
                loading={loading}
                columns={columns}
                dataSource={applications}
                rowKey="_id"
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  pageSizeOptions: ["5", "10", "20", "50"],
                  total: pagination.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `Total ${total} items`,
                }}
                onChange={handleTableChange}
              />
            </div>
          </div>
        </div>
      </div>

<Modal
  zIndex={1000}
  title={<span className="text-lg font-semibold text-gray-800">Apply for Job</span>}
  open={openApplyModal}
  onCancel={() => {
    setOpenApplyModal(false);
    form.resetFields();
  }}
  footer={null}
  maskClosable={false}
  closable={true}
  width={600}
  className="rounded-lg shadow-xl"
  bodyStyle={{ padding: "24px", backgroundColor: "#f9fafb" }}
>
  <Form
    form={form}
    onFinish={onApplyFinish}
    layout="vertical"
    className="space-y-4"
  >
    <Form.Item
      name="job_id"
      label={<span className="text-sm font-medium text-gray-700">Job Posting</span>}
      rules={[{ required: true, message: "Please select a job" }]}
    >
   <Select
              placeholder="Select a job"
              showSearch
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
                optionLabelProp="label" 
            >
              {jobPostings.map((job) => (
                <Select.Option key={job._id} value={job._id} label={job.title}>
                  {renderJobOption(job)}
                </Select.Option>
              ))}
            </Select>

    </Form.Item>
    <Form.Item
      name="candidate_name"
      label={<span className="text-sm font-medium text-gray-700">Candidate Name</span>}
      rules={[{ required: true, message: "Please enter your name" }]}
    >
      <Input
        placeholder="Enter your name"
        className="w-full rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
      />
    </Form.Item>
    <Form.Item
      name="email"
      label={<span className="text-sm font-medium text-gray-700">Email</span>}
      rules={[
        { required: true, message: "Please enter your email" },
        { type: "email", message: "Please enter a valid email" },
      ]}
    >
      <Input
        placeholder="Enter your email"
        className="w-full rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
      />
    </Form.Item>
    <Form.Item
      name="resume"
      label={<span className="text-sm font-medium text-gray-700">Resume (PDF only)</span>}
      valuePropName="file"
      rules={[{ required: true, message: "Please upload your resume" }]}
    >
      <Upload
        accept=".pdf"
        maxCount={1}
        beforeUpload={() => false}
        className="w-full"
      >
        <Button className="w-full bg-indigo-600 text-white hover:bg-indigo-700 rounded-md">
          Upload Resume
        </Button>
      </Upload>
    </Form.Item>
    <Form.List name="qualifications">
      {(fields, { add, remove }) => (
        <div className="space-y-2">
          {fields.map(({ key, name, ...restField }) => (
            <div
              key={key}
              className="flex items-center space-x-2"
            >
              <Form.Item
                {...restField}
                name={[name, "name"]}
                rules={[{ required: true, message: "Please enter qualification name" }]}
                className="flex-1 mb-0"
              >
                <Input
                  placeholder="Qualification Name (e.g., Experience, MERN)"
                  className="rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </Form.Item>
              <Form.Item
                {...restField}
                name={[name, "level"]}
                rules={[{ required: true, message: "Please enter your level" }]}
                className="flex-1 mb-0"
              >
                <Input
                  placeholder="Your Level (e.g., 3 years, 8)"
                  className="rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </Form.Item>
              <Button
                type="link"
                onClick={() => remove(name)}
                className="text-red-500 hover:text-red-700"
              >
                Remove
              </Button>
            </div>
          ))}
          <Button
            type="dashed"
            onClick={() => add()}
            block
            className="mt-2 border-indigo-300 text-indigo-600 hover:bg-indigo-50 rounded-md"
          >
            Add Qualification
          </Button>
        </div>
      )}
    </Form.List>
    <Form.Item className="mt-6">
      <div className="flex justify-end space-x-2">
        <Button
          onClick={() => {
            setOpenApplyModal(false);
            form.resetFields();
          }}
          className="h-10 bg-gray-200 text-gray-800 hover:bg-gray-300 rounded-md"
        >
          Cancel
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          className="h-10 bg-indigo-600 text-white hover:bg-indigo-700 rounded-md"
        >
          Submit Application
        </Button>
      </div>
    </Form.Item>
  </Form>
</Modal>

      <Modal
        zIndex={1100}
        title={<span className="text-lg font-semibold text-gray-800">Application Details</span>}
        open={openDetailsModal}
        onCancel={() => {
          setOpenDetailsModal(false);
          setResumeText("");
        }}
        footer={[
          <Button
            key="close"
            className="h-10 bg-gray-200 text-gray-800 hover:bg-gray-300 rounded-md"
            onClick={() => {
              setOpenDetailsModal(false);
              setResumeText("");
            }}
          >
            Close
          </Button>,
        ]}
        width={600}
        className="rounded-lg shadow-xl"
        bodyStyle={{ padding: "24px", backgroundColor: "#f9fafb" }}
      >
        {selectedApplication && (
          <div className="space-y-4 text-gray-700">
            <p><strong className="font-medium">Job Title:</strong> {selectedApplication.job_id?.title || "N/A"}</p>
            <p><strong className="font-medium">Candidate Name:</strong> {selectedApplication.candidate_name}</p>
            <p><strong className="font-medium">Email:</strong> {selectedApplication.email}</p>
            <p><strong className="font-medium">Qualifications:</strong> {selectedApplication.qualifications?.length ? selectedApplication.qualifications.map((q) => `${q.name}: ${q.level}`).join(", ") : "N/A"}</p>
            <p><strong className="font-medium">Status:</strong> {selectedApplication.status}</p>
            <p><strong className="font-medium">Applied Date:</strong> {selectedApplication.applied_at ? moment(selectedApplication.applied_at).format("YYYY-MM-DD") : "N/A"}</p>
            <p><strong className="font-medium">Resume:</strong> {selectedApplication.resume?.originalName || "N/A"}</p>
            {selectedApplication.resume?.contentType === "application/pdf" && (
              <Button
                className="h-10 bg-indigo-600 text-white hover:bg-indigo-700 rounded-md"
                onClick={() => handleDownloadResume(selectedApplication.resume)}
              >
                Download Resume
              </Button>
            )}
            <p><strong className="font-medium">Resume Content:</strong></p>
            <div className="max-h-64 overflow-y-auto border border-gray-200 p-4 bg-white rounded-md shadow-sm font-mono text-sm">
              <pre className="whitespace-pre-wrap">{resumeText || "No resume text available"}</pre>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        zIndex={1200}
        title={<span className="text-lg font-semibold text-gray-800">Qualification Match Results</span>}
        open={openMatchModal}
        onCancel={() => {
          setOpenMatchModal(false);
          setMatchResults(null);
        }}
        footer={[
          <Button
            key="close"
            className="h-10 bg-gray-200 text-gray-800 hover:bg-gray-300 rounded-md"
            onClick={() => {
              setOpenMatchModal(false);
              setMatchResults(null);
            }}
          >
            Close
          </Button>,
        ]}
        width={600}
        className="rounded-lg shadow-xl"
        bodyStyle={{ padding: "24px", backgroundColor: "#f9fafb" }}
      >
        {matchResults && (
          <div className="space-y-4 text-gray-700">
            <p><strong className="font-medium">Match Percentage:</strong> {matchResults.matchPercentage}%</p>
            <h4 className="font-semibold text-gray-800">Matches:</h4>
            {matchResults.matches?.length ? (
              <ul className="list-disc pl-5">
                {matchResults.matches.map((match, index) => (
                  <li key={index} className="mb-2">
                    <strong>{match.name}:</strong> Expected: {match.expectedLevel}, Applicant: {match.applicantLevel}, Match: {match.match ? <span className="text-green-600">Yes</span> : <span className="text-red-600">No</span>}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No matches found.</p>
            )}
            <h4 className="font-semibold text-gray-800">Mismatches:</h4>
            {matchResults.mismatches?.length ? (
              <ul className="list-disc pl-5">
                {matchResults.mismatches.map((mismatch, index) => (
                  <li key={index} className="mb-2">
                    <strong>{mismatch.name}:</strong> Expected: {mismatch.expectedLevel}, Applicant: {mismatch.applicantLevel}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No mismatches found.</p>
            )}
          </div>
        )}
      </Modal>

      <Modal
        zIndex={1300}
        title={<span className="text-lg font-semibold text-gray-800">Change Page Size</span>}
        open={pageSizeModalVisible}
        onOk={handlePageSizeChange}
        onCancel={() => setPageSizeModalVisible(false)}
        footer={[
          <Button
            key="cancel"
            className="h-10 bg-gray-200 text-gray-800 hover:bg-gray-300 rounded-md"
            onClick={() => setPageSizeModalVisible(false)}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            className="h-10 bg-indigo-600 text-white hover:bg-indigo-700 rounded-md"
            onClick={handlePageSizeChange}
          >
            Apply
          </Button>,
        ]}
        width={400}
        className="rounded-lg shadow-xl"
        bodyStyle={{ padding: "24px", backgroundColor: "#f9fafb" }}
      >
        <div className="form-group">
          <label className="form-label font-medium text-gray-700">Items per page:</label>
          <Select
            className="w-full h-10 rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
            value={tempPageSize}
            onChange={(value) => setTempPageSize(value)}
          >
            {pageSizeOptions.map((size) => (
              <Select.Option key={size} value={size}>
                {size}
              </Select.Option>
            ))}
          </Select>
        </div>
      </Modal>
    </div>
  );
};

export default Applications;